import { useEffect } from "react";

const CHUNK_ERROR_RE =
  /Loading chunk \d+ failed|ChunkLoadError|Loading CSS chunk \d+ failed/i;

// Path (no origin/query) of a Next build asset. Matched against a URL's
// pathname after confirming it's same-origin, so only our own build's chunks
// qualify — not a third-party script whose URL happens to contain the segment.
// Assumes the default asset path; if a `basePath`/`assetPrefix` is ever set in
// next.config, prepend it here or resource-error detection will silently miss.
const CHUNK_PATH_RE = /^\/_next\/static\/(?:chunks|css)\/.+\.(?:js|css)$/;

export const RELOAD_KEY = "nf:chunk-reload";
// Errors recurring within this window of a reload are treated as the same
// incident (a genuinely missing chunk, or a CDN node lagging the deploy),
// not a fresh stale-deploy hit, so their reloads count toward the burst cap.
const LOOP_WINDOW_MS = 30_000;
// Give up after this many reloads in one burst and let the error surface, so a
// persistently broken build doesn't rapid-reload-loop.
const MAX_RELOAD_ATTEMPTS = 2;
// Absolute ceiling across the whole tab session (never reset). Bounds the
// slow-drip case the burst cap misses — a failure recurring just outside the
// window would otherwise reset the burst count and reload indefinitely.
const MAX_RELOADS_PER_SESSION = 5;

// Thrown values reach us from three places — an `error.tsx`/`global-error`
// boundary (Error), `window.onerror` (ErrorEvent), and `unhandledrejection`
// (the rejection reason, which can be anything). We only ever read `name` and
// `message`, so a structural shape covers all of them without `unknown`.
type PossibleChunkError =
  | { name?: string; message?: string }
  | null
  | undefined;

export function isChunkLoadError(error: PossibleChunkError): boolean {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  return (
    typeof error.message === "string" && CHUNK_ERROR_RE.test(error.message)
  );
}

/**
 * A resource `error` event whose target is a same-origin Next chunk
 * `<script>`/`<link>`. These fire only in the capture phase and carry no
 * thrown Error, so `isChunkLoadError` can't see them. Reads `src`/`href` off
 * the target, so keep it scoped to the capture-phase window listener.
 */
export function isChunkResourceError(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as Partial<HTMLScriptElement & HTMLLinkElement>;
  const url = el.src ?? el.href;
  if (typeof url !== "string") return false;
  try {
    const parsed = new URL(url, window.location.href);
    return (
      parsed.origin === window.location.origin &&
      CHUNK_PATH_RE.test(parsed.pathname)
    );
  } catch {
    // No window (SSR) or an unparseable URL — resource errors only fire in the
    // browser, so treat anything we can't resolve as not-ours.
    return false;
  }
}

// `count` is the current burst's attempts (resets between incidents); `total`
// is the never-reset session ceiling counter.
type ReloadState = { at: number; count: number; total: number };

function readReloadState(): ReloadState | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      at?: number;
      count?: number;
      total?: number;
    };
    if (typeof parsed.at === "number" && typeof parsed.count === "number") {
      return {
        at: parsed.at,
        count: parsed.count,
        total: typeof parsed.total === "number" ? parsed.total : parsed.count,
      };
    }
  } catch {
    // malformed or unavailable — treat as no prior state
  }
  return null;
}

// Single source of truth for the cap logic, shared by the read-only
// canReloadForChunkError() (render-time) and the mutating reloadForChunkError()
// (effect-time) so the two can't drift. Pure: given the recorded state and the
// current time, decides whether a reload is allowed and what the next state is.
function decideReload(
  state: ReloadState | null,
  now: number,
): { allowed: boolean; next: ReloadState } {
  const total = state?.total ?? 0;
  // Hard stop for the slow-drip case: never exceed the session ceiling.
  if (total >= MAX_RELOADS_PER_SESSION) {
    return { allowed: false, next: state ?? { at: now, count: 0, total } };
  }
  if (state && now - state.at < LOOP_WINDOW_MS) {
    // Same burst: the previous reload didn't fix it. Stop after the cap so a
    // genuinely broken build surfaces its error instead of rapid-looping.
    if (state.count >= MAX_RELOAD_ATTEMPTS)
      return { allowed: false, next: state };
    return {
      allowed: true,
      next: { at: now, count: state.count + 1, total: total + 1 },
    };
  }
  // First failure, or a new incident long after the last reload — reset the
  // burst count but keep accruing the session total.
  return { allowed: true, next: { at: now, count: 1, total: total + 1 } };
}

/**
 * Read-only check of whether another reload is allowed under the burst/session
 * caps — no mutation, safe to call during render (so a concurrent re-render
 * can't miscount). Boundaries call this during render so they show the
 * reloading state only when a reload is actually imminent, and fall through to
 * the normal error UI (with retry) once the caps are exhausted.
 */
export function canReloadForChunkError(): boolean {
  return decideReload(readReloadState(), Date.now()).allowed;
}

/** Triggers a hard reload if the caps allow. Returns whether it reloaded. */
export function reloadForChunkError(): boolean {
  const { allowed, next } = decideReload(readReloadState(), Date.now());
  if (!allowed) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage unavailable (private mode / SSR) — reload anyway; the
    // loop-guard is an optimization, not a correctness requirement.
  }
  window.location.reload();
  return true;
}

/**
 * Attaches the window-level listeners that recover from chunk failures firing
 * outside React's render path — rejected `import()`s and failed prefetch
 * `<script>`/`<link>` loads. Returns an unsubscribe fn for effect cleanup.
 * Takes the target so it can be unit-tested with a fake window.
 */
export function subscribeToChunkErrors(
  target: Pick<Window, "addEventListener" | "removeEventListener">,
): () => void {
  const onError = (event: ErrorEvent) => {
    // Three match paths:
    // 1. event.error — the thrown ChunkLoadError (the common case).
    // 2. event (message) — some browsers fire with event.error === null but
    //    the "Loading chunk N failed" text still on event.message; our chunks
    //    are same-origin, so it isn't redacted to "Script error.".
    // 3. event.target — a failed <script>/<link> resource load, which fires no
    //    thrown Error and only in the capture phase (hence capture: true).
    if (
      isChunkLoadError(event.error) ||
      isChunkLoadError(event) ||
      isChunkResourceError(event.target)
    ) {
      reloadForChunkError();
    }
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) reloadForChunkError();
  };
  // Capture phase so resource-load failures (which don't bubble) are seen.
  target.addEventListener("error", onError, { capture: true });
  target.addEventListener("unhandledrejection", onRejection);
  return () => {
    target.removeEventListener("error", onError, { capture: true });
    target.removeEventListener("unhandledrejection", onRejection);
  };
}

/**
 * Shared by every React error boundary that a stale-deploy chunk failure can
 * land in — `global-error.jsx` and the per-route-group `error.tsx` files. A
 * nested `error.tsx` catches the error before `global-error` ever sees it, so
 * each boundary must run this itself.
 *
 * Returns whether the boundary should show the reloading state: true only when
 * the error is a chunk failure AND a reload is still allowed under the caps.
 * Once the caps are exhausted this returns false so the boundary falls through
 * to its normal error UI (with retry) instead of hanging on the reload spinner.
 */
export function useChunkErrorRecovery(error: PossibleChunkError): boolean {
  const isRecovering = isChunkLoadError(error) && canReloadForChunkError();
  useEffect(() => {
    if (isRecovering) reloadForChunkError();
  }, [isRecovering]);
  return isRecovering;
}
